import { Modal } from 'react-bootstrap';

interface FAQModalProps {
  show: boolean;
  onHide: () => void;
}

const FAQModal: React.FC<FAQModalProps> = ({ show, onHide }) => {
  return (
    <Modal
      show={show}
      onHide={onHide}
      id="modalFAQ"
      size="xl"
      centered
      scrollable
      backdrop="static"
      keyboard={false}
      className="FAQ-modal"
      style={{ paddingRight: '0px' }}
      contentClassName="v3-dialog"
    >
      <Modal.Header>
        <Modal.Title as="h4">aosphere&apos;s Intelligent Search</Modal.Title>
        <button
          type="button"
          className="btn-close btn-close-white"
          onClick={onHide}
          aria-label="Close"
        />
      </Modal.Header>
      <Modal.Body>
        <div className="ao_labelBold">
          <div style={{ fontWeight: 'bold', marginBottom: '1rem' }}>What is Intelligent Search?</div>
          <p>
            Intelligent Search is the name for aosphere&apos;s new natural language search beta function. Users can query aosphere&apos;s trusted legal analysis using natural language, and Intelligent Search provides a direct answer, plus links to the relevant sections of the underlying memo from local counsel.
          </p>
          <p>
            Intelligent Search uses only information contained in aosphere&apos;s trusted legal analysis – it does not use information from the internet, which can be incorrect or out of date. The key sources of information used by Intelligent Search are the underlying memos from local counsel and the colour-coded reports (traffic light reports).
          </p>
          <p>
            Intelligent Search is currently a beta and is still subject to further development and iteration.
          </p>

          <div style={{ fontWeight: 'bold', marginTop: '1.5rem', marginBottom: '1rem' }}>What key principles underpin Intelligent Search?</div>
          <ul>
            <li>
              <strong>Accuracy</strong> – we understand how important it is for the summary answers generated by Intelligent Search to be accurate. That&apos;s why:
              <ul>
                <li>The system will only propose an answer if it has a high degree of confidence,</li>
                <li>Answers are labelled &quot;Provisional AI-Generated Answer&quot;, and</li>
                <li>Our team of aosphere lawyers can review the output of Intelligent Search using our custom-made Supervision App, e.g. in relation to feedback that users can provide directly into the system.</li>
              </ul>
            </li>
            <li>
              <strong>Transparency</strong> – Intelligent Search cites the sections of the underlying memo that it has used in preparing an answer to a question, so users always know where the answer has come from.
            </li>
          </ul>

          <div style={{ fontWeight: 'bold', marginTop: '1.5rem', marginBottom: '1rem' }}>How is AI used in Intelligent Search?</div>
          <ul>
            <li>A semantic-based AI query parser uses natural language processing to analyse the user&apos;s query to understand the user&apos;s query and intent.</li>
            <li>AI analyses the parsed query and decides whether to respond to the question, or reject it as out-of-scope.</li>
            <li>
              Currently, queries will be rejected if:
              <ul>
                <li>
                  They relate to more than one jurisdiction. As the aosphere analysis is provided on a per jurisdiction basis, version 1 of Intelligent Search can deal only with questions about a single jurisdiction.<br />
                  <em>For example, &quot;Do I need a DPO in Thailand&quot; will be accepted, but &quot;Which jurisdictions in APAC have DPO requirements&quot; will be rejected.</em><br />
                  <strong>Note:</strong> We are looking at handling queries that relate to multiple jurisdictions in future versions of Intelligent Search.
                </li>
                <li>
                  They relate to questions outside the scope of the relevant aosphere product.<br />
                  <em>For example, &quot;Is there a confidentiality law in Australia&quot; will be rejected by Intelligent Search for Data Privacy, as the question is not about the data privacy matters covered by the Data Privacy product.</em>
                </li>
              </ul>
            </li>
            <li>
              Intelligent Search is aware of other features on the aoslogin platform that can assist with multi-jurisdictional questions, such as the compare function, trackers and apps, and will refer the user to these tools as appropriate.
            </li>
            <li>
              AI identifies the sections of the underlying memo which are relevant to the query, summarises those sections into a short, direct answer to the question, and references those sections so that the sources of the direct answer are transparent, and the user can quickly access the underlying detail.
            </li>
            <li>
              Alongside the search results, users will also be presented with related searches that may be of interest (&quot;People also ask&quot;) and a glossary of relevant terms.
            </li>
          </ul>

          <div style={{ fontWeight: 'bold', marginTop: '1.5rem', marginBottom: '1rem' }}>How is the accuracy of Intelligent Search answers monitored?</div>
          <p>
            Intelligent Search uses generative AI to provide short, direct answers to user&apos;s questions. Intelligent Search will only offer a direct answer if it has a high degree of confidence in that answer (&gt;90%), but generative AI can make mistakes and miss important nuance.
          </p>
          <p>
            To mitigate the risk of errors, the system clearly identifies, and provides links to, the relevant sections of the underlying memo which have been used as the basis for the Provisional AI-Generated Answer.
          </p>
          <p>
            <strong>Users should always check the underlying memorandum when placing emphasis on a Provisional AI-Generated answer.</strong>
          </p>
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default FAQModal;

